import consumerApi from "api/services/consumer-api";
import consumerApiCa from "api/services/consumer-api-ca";
import { Brand } from "custom-types/Brand";
import { Coordinates } from "custom-types/Coordinates";
import { MenuItem } from "custom-types/MenuItem";
import { Strain } from "custom-types/Strain";
import logError from "utils/logError";

/**
  Q:  Why are the types here?
  A:  These types represent what is expected from the /api/brands/v1/[:brand_slug]/product_catalog endpoint.

      This endpoint is unlikely to be used for anything but Brand Profile Page.  Until agreement on
      which endpoints represent "canonical" data in shared SPA, do not be polluting the custom-types dir.
*/

export type BrandCatalogCategory = {
  categorySlug: string;
  categoryName: string;
  productCount: number;
};

export type BrandCatalogData = {
  metadata: { categories: BrandCatalogCategory[]; totalCount: number };
  data: BrandCatalogProduct[];
};

export type BrandCatalogProduct = {
  avgApprovedPublicReviewRating: number;
  categoryName: string;
  countApprovedPublicReviews: number;
  id: number;
  name: string;
  shortDescription: string;
  subcategoryName: string;
  thumbnailImageUrl: string;
  imageUrl: string;
  brand: Brand;
  bestOfferVariant?: MenuItem;
  slug: string;
  strain?: Strain;
  thcContent?: string;
  cbdContent?: string;
};

type Props = {
  coordinates: Coordinates;
  brandSlug: string;
  categorySlug?: string;
  take?: number;
  skip?: number;
  countryCode: string;
};

export const DEFAULT_PRODUCTS_PER_PAGE = 24;

export const getProductCatalog = async ({
  coordinates,
  brandSlug,
  categorySlug,
  take = DEFAULT_PRODUCTS_PER_PAGE,
  skip,
  countryCode,
}: Props) => {
  const api = countryCode === "CA" ? consumerApiCa : consumerApi;

  let qstring = `?lat=${encodeURIComponent(
    coordinates.lat
  )}&lon=${encodeURIComponent(coordinates.lon)}`;

  if (categorySlug) {
    qstring += `&category_slug=${encodeURIComponent(categorySlug)}`;
  }

  if (take) {
    qstring += `&take=${encodeURIComponent(take)}`;
  }

  if (skip) {
    qstring += `&skip=${encodeURIComponent(skip)}`;
  }
  try {
    const { data } = await api.get<BrandCatalogData>(
      `/api/brands/v1/${brandSlug}/product_catalog${qstring}`
    );

    return data;
  } catch (e) {
    logError(e.message, {
      functionName: "getProductCatalog",
      service: "consumer",
      statusCode: e.statusCode,
    });

    return {
      data: [],
      metadata: { categories: [], totalCount: 0 },
    };
  }
};
